// Topic View - Individual forum topic with replies and comments
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PublicLayout from '../../public/PublicLayout';

const TopicView = ({ user }) => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');
  
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const arcaGreen = '#6B8E23';
  const arcaLightGreen = '#E8F5E9';

  useEffect(() => {
    fetchTopic();
  }, [topicId]);

  const fetchTopic = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/communication/forum/topics/${topicId}`);
      const data = await response.json();
      
      if (data.success) {
        setTopic(data.data.topic);
        setPosts(data.data.posts);
      } else {
        toast.error('Topic not found');
        navigate('/community');
      }
    } catch (error) {
      console.error('Error fetching topic:', error);
      toast.error('Failed to load topic');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (contentType, contentId, currentlyLiked) => {
    if (!user) {
      toast.warning('Please login to like content');
      return;
    }

    try {
      const token = localStorage.getItem('userToken') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (!token) {
        toast.warning('Please login to like content');
        return;
      }
      const response = await fetch(`${API_BASE}/communication/forum/${contentType}/${contentId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        // Update local state
        if (contentType === 'forum_topic') {
          setTopic({
            ...topic,
            like_count: data.liked ? topic.like_count + 1 : topic.like_count - 1
          });
        } else {
          setPosts(posts.map(post => 
            post.id === contentId 
              ? { ...post, like_count: data.liked ? post.like_count + 1 : post.like_count - 1 }
              : post
          ));
        }
        toast.success(data.liked ? 'Liked!' : 'Unliked');
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Failed to update like');
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.warning('Please login to reply');
      return;
    }

    if (!replyContent.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    try {
      const token = localStorage.getItem('userToken') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return;
      }
      const response = await fetch(`${API_BASE}/communication/forum/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          topic_id: topicId,
          content: replyContent,
          parent_post_id: replyingTo
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Reply posted!');
        setReplyContent('');
        setReplyingTo(null);
        fetchTopic(); // Refresh to get new post
      } else {
        toast.error(data.message || 'Failed to post reply');
      }
    } catch (error) {
      console.error('Error posting reply:', error);
      toast.error('Failed to post reply');
    }
  };

  const handleEdit = async (postId) => {
    if (!editContent.trim()) {
      toast.error('Content cannot be empty');
      return;
    }

    try {
      const token = localStorage.getItem('userToken') || localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return;
      }
      const response = await fetch(`${API_BASE}/communication/forum/posts/${postId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: editContent })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Post updated!');
        setEditingPost(null);
        setEditContent('');
        fetchTopic();
      }
    } catch (error) {
      console.error('Error editing post:', error);
      toast.error('Failed to edit post');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" style={{ color: arcaGreen }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="text-center py-5">
        <h4>Topic not found</h4>
        <button className="btn btn-primary mt-3" onClick={() => navigate('/community')}>
          Back to Community
        </button>
      </div>
    );
  }

  const getCategoryColor = (color) => {
    const colorMap = {
      blue: { bg: '#e3f2fd', text: '#1976d2' },
      green: { bg: '#e8f5e9', text: '#388e3c' },
      purple: { bg: '#f3e5f5', text: '#7b1fa2' },
      yellow: { bg: '#fff8e1', text: '#f57f17' },
      red: { bg: '#ffebee', text: '#c62828' },
      indigo: { bg: '#e8eaf6', text: '#3f51b5' }
    };
    return colorMap[color] || colorMap.blue;
  };

  const colors = getCategoryColor(topic.category_color);

  return (
    <PublicLayout>
      <div className="container py-4">
      {/* Back Button */}
      <button 
        className="btn btn-outline-secondary mb-4"
        onClick={() => navigate('/community/forums')}
      >
        <i className="fas fa-arrow-left me-2"></i>Back to Forums
      </button>

      {/* Topic Card */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white border-bottom py-3">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <span 
                className="badge mb-2"
                style={{ backgroundColor: colors.bg, color: colors.text }}
              >
                {topic.category_name}
              </span>
              <h2 className="h4 fw-bold mb-0">{topic.title}</h2>
            </div>
            {topic.is_pinned && (
              <span className="badge bg-warning">
                <i className="fas fa-thumbtack me-1"></i>Pinned
              </span>
            )}
          </div>
        </div>
        
        <div className="card-body p-4">
          <div className="d-flex mb-3">
            <img 
              src={topic.author_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(topic.author_name)}&background=6B8E23&color=fff`}
              alt={topic.author_name}
              className="rounded-circle me-3"
              style={{ width: '56px', height: '56px', objectFit: 'cover' }}
            />
            <div>
              <h5 className="mb-1 fw-bold">{topic.author_name}</h5>
              <p className="text-muted small mb-0">
                <i className="fas fa-clock me-1"></i>
                {new Date(topic.created_at).toLocaleString()}
              </p>
            </div>
          </div>
          
          <div className="mb-4">
            <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{topic.content}</p>
          </div>

          {/* Topic Stats & Actions */}
          <div className="d-flex justify-content-between align-items-center border-top pt-3">
            <div className="d-flex gap-4 text-muted small">
              <span><i className="fas fa-eye me-1"></i>{topic.view_count} views</span>
              <span><i className="fas fa-comments me-1"></i>{topic.reply_count} replies</span>
              <span><i className="fas fa-heart me-1"></i>{topic.like_count} likes</span>
            </div>
            <button 
              className={`btn btn-sm ${topic.liked ? 'btn-danger' : 'btn-outline-danger'}`}
              onClick={() => handleLike('forum_topic', topic.id, topic.liked)}
            >
              <i className="fas fa-heart me-1"></i>
              {topic.liked ? 'Liked' : 'Like'}
            </button>
          </div>
        </div>
      </div>

      {/* Replies Section */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0 fw-bold">
            <i className="fas fa-comments me-2"></i>
            {posts.length} {posts.length === 1 ? 'Reply' : 'Replies'}
          </h5>
        </div>
        
        <div className="card-body p-4">
          {/* Reply Form */}
          {user ? (
            <div className="mb-4 pb-4 border-bottom">
              <form onSubmit={handleReply}>
                <div className="d-flex">
                  <img 
                    src={user.profilePicture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.firstName + ' ' + user.lastName)}&background=6B8E23&color=fff`}
                    alt="You"
                    className="rounded-circle me-3"
                    style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                  />
                  <div className="flex-grow-1">
                    <textarea
                      className="form-control mb-2"
                      rows="3"
                      placeholder={replyingTo ? "Write your reply..." : "Add a comment..."}
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                    ></textarea>
                    <div className="d-flex justify-content-between">
                      {replyingTo && (
                        <button 
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => setReplyingTo(null)}
                        >
                          Cancel Reply
                        </button>
                      )}
                      <button 
                        type="submit"
                        className="btn text-white ms-auto"
                        style={{ backgroundColor: arcaGreen }}
                      >
                        <i className="fas fa-paper-plane me-2"></i>Post Reply
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          ) : (
            <div className="alert alert-info mb-4">
              <i className="fas fa-info-circle me-2"></i>
              Please <strong>login</strong> to post a reply
            </div>
          )}

          {/* Posts List */}
          {posts.length > 0 ? (
            <div className="list-group list-group-flush">
              {posts.map((post, index) => (
                <div key={post.id} className={`list-group-item px-0 ${index !== posts.length - 1 ? 'border-bottom' : ''} py-4`}>
                  <div className="d-flex">
                    <img 
                      src={post.author_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(post.author_name)}&background=6B8E23&color=fff`}
                      alt={post.author_name}
                      className="rounded-circle me-3"
                      style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                    />
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h6 className="mb-0 fw-bold">{post.author_name}</h6>
                          <small className="text-muted">
                            {new Date(post.created_at).toLocaleString()}
                            {post.edited_at && <span className="ms-2">(edited)</span>}
                          </small>
                        </div>
                        {user && user.id === post.author_id && (
                          <button 
                            className="btn btn-sm btn-link text-muted"
                            onClick={() => {
                              setEditingPost(post.id);
                              setEditContent(post.content);
                            }}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                        )}
                      </div>
                      
                      {editingPost === post.id ? (
                        <div>
                          <textarea
                            className="form-control mb-2"
                            rows="3"
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                          ></textarea>
                          <div className="d-flex gap-2">
                            <button 
                              className="btn btn-sm text-white"
                              style={{ backgroundColor: arcaGreen }}
                              onClick={() => handleEdit(post.id)}
                            >
                              Save
                            </button>
                            <button 
                              className="btn btn-sm btn-secondary"
                              onClick={() => {
                                setEditingPost(null);
                                setEditContent('');
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="mb-3" style={{ whiteSpace: 'pre-wrap' }}>{post.content}</p>
                          
                          <div className="d-flex gap-3">
                            <button 
                              className="btn btn-sm btn-link text-muted p-0"
                              onClick={() => handleLike('forum_post', post.id, post.liked)}
                            >
                              <i className={`fas fa-heart me-1 ${post.liked ? 'text-danger' : ''}`}></i>
                              {post.like_count}
                            </button>
                            {user && (
                              <button 
                                className="btn btn-sm btn-link text-muted p-0"
                                onClick={() => setReplyingTo(post.id)}
                              >
                                <i className="fas fa-reply me-1"></i>Reply
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-muted">
              <i className="fas fa-comments fa-3x mb-3 d-block"></i>
              <p>No replies yet. Be the first to comment!</p>
            </div>
          )}
        </div>
      </div>
      </div>
    </PublicLayout>
  );
};

export default TopicView;
